import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  companyName?: string;
  role: string;
  emailVerified: boolean;
  cardVerified?: boolean;
  createdAt?: string;
}

export interface Customer {
  id: string;
  companyName?: string;
  taxId?: string;
  billingAddress?: any;
  creditBalance: number;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

interface AuthContextType {
  user: User | null;
  customer: Customer | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  updateProfile: (userData: Partial<User & Customer>) => Promise<void>;
  updateUser: (userData: Partial<User>) => Promise<void>;
  refreshUser: () => Promise<void>;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  companyName?: string;
  recaptchaToken?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      console.log('🔍 Checking auth status...');
      const token = localStorage.getItem('token');
      console.log('🔑 Token exists:', !!token);
      
      if (!token) {
        console.log('❌ No token found, skipping profile check');
        setLoading(false);
        return;
      }
      
      const profile = await authService.getProfile();
      console.log('📝 Profile response:', profile);
      if (profile.success) {
        console.log('✅ Auth successful, user:', profile.data.user);
        setUser(profile.data.user);
        setCustomer(profile.data.customer);
      } else {
        console.log('❌ Auth failed:', profile.error);
        // Clear invalid token
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error('💥 Auth check failed:', error);
      // Clear invalid token on error
      localStorage.removeItem('token');
    } finally {
      console.log('⏹️ Auth check complete, setting loading to false');
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const response = await authService.login(email, password);
      
      if (response.success) {
        setUser(response.data.user);
        setCustomer(response.data.customer);
      } else {
        throw new Error(response.error || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: RegisterData) => {
    setLoading(true);
    try {
      const response = await authService.register(userData);
      if (!response.success) {
        throw new Error(response.error || 'Registration failed');
      }
      // Do NOT set the user/customer into auth state here. The account is
      // created but unverified, and authService.register no longer stores a
      // token. Logging the user in at this point would bypass email
      // verification. The Register page shows a "check your email" screen;
      // the user becomes authenticated only after verifying + signing in.
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    try {
      authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setCustomer(null);
      localStorage.removeItem('token');
    }
  };

  const updateProfile = async (userData: Partial<User & Customer>) => {
    try {
      const response = await authService.updateProfile(userData);
      if (response.success) {
        setUser(response.data.user);
        setCustomer(response.data.customer);
      } else {
        throw new Error(response.error || 'Profile update failed');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  };

  const updateUser = async (userData: Partial<User>) => {
    try {
      const response = await authService.updateProfile(userData);
      if (response.success) {
        setUser(response.data.user);
        if (response.data.customer) {
          setCustomer(response.data.customer);
        }
      } else {
        throw new Error(response.error || 'User update failed');
      }
    } catch (error) {
      console.error('User update error:', error);
      throw error;
    }
  };

  const refreshUser = async () => {
    try {
      const profile = await authService.getProfile();
      if (profile.success) {
        setUser(profile.data.user);
        setCustomer(profile.data.customer);
      }
    } catch (error) {
      console.error('Profile refresh failed:', error);
    }
  };

  const value: AuthContextType = {
    user,
    customer,
    loading,
    login,
    register,
    logout,
    updateProfile,
    updateUser,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};